import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";

import { NavigationService } from "#root/core/navigation/navigation.service";
import { Navigation } from "#root/core/navigation/navigation.types";
import { Subject, takeUntil } from "rxjs";

import { APP_CONFIG } from "#root/config/constants/app-config.constant";
import { COMPANY_CONFIG } from "#root/config/constants/company-config.constant";
import { AppEnvConfig } from "#root/config/interfaces/app-config.interface";
import { CompanyConfig } from "#root/config/interfaces/company-config.interface";
import { AppConfig, Scheme } from "#root/core/config/app.config";
import { FormBuilder, FormGroup } from "@angular/forms";
import {
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from "@fuse/components/navigation";
import { FuseConfigService } from "@fuse/services/config";
import { FuseMediaWatcherService } from "@fuse/services/media-watcher";
import { environment } from "environments/environment";

@Component({
  selector: "dense-layout",
  templateUrl: "./dense.component.html",
  encapsulation: ViewEncapsulation.None,
})
export class DenseLayoutComponent implements OnInit, OnDestroy {
  private readonly unsubscribeAll: Subject<void> = new Subject<void>();
  private readonly _router = inject(Router);
  private readonly _navigationService = inject(NavigationService);
  private readonly _fuseMediaWatcherService = inject(FuseMediaWatcherService);
  private readonly _fuseNavigationService = inject(FuseNavigationService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly fuseConfigurationService = inject(FuseConfigService);

  private unmodifiedNavigation: Navigation;

  public form: FormGroup = this.formBuilder.group({
    search: [""],
  });
  public config: AppConfig;
  public navigation: Navigation;
  public navigationAppearance: "default" | "dense" = "dense";
  public isScreenSmall: boolean;
  public logo: string = APP_CONFIG.isotype;
  public logoIso: string = APP_CONFIG.isotype;
  public logoNormal: string = APP_CONFIG.logoDark;
  public isLogoIso: boolean = true;
  public companyConfigConstant: CompanyConfig = COMPANY_CONFIG;
  public appConfigConstant: AppEnvConfig = APP_CONFIG;
  public appVersion: string = environment.version;
  readonly isProduction = environment.production

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to navigation data
    this._navigationService.navigation$
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe((navigation: Navigation) => {
        this.unmodifiedNavigation = navigation;
        this.navigation = navigation;
      });

    this.searchChanges();
    this.onClickItem();

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes("md");

        // Change the navigation appearance
        this.navigationAppearance = this.isScreenSmall ? "default" : "dense";
      });

    this.fuseConfigurationService.config$
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe((config: AppConfig) => {
        this.config = config;
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.unsubscribeAll.next(null);
    this.unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        name,
      );

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  /**
   * Toggle the navigation appearance
   */
  toggleNavigationAppearance(): void {
    if (this.navigationAppearance === "dense") {
      this.logo = this.logo != this.logoIso ? this.logoIso : this.logoNormal;
      if (this.logo === this.logoIso) {
        this.isLogoIso = true;
      } else {
        this.isLogoIso = false;
      }
    } else {
      this.logo = this.logo != this.logoNormal ? this.logoNormal : this.logoIso;
      if (this.logo === this.logoNormal) {
        this.isLogoIso = false;
      } else {
        this.isLogoIso = true;
      }
    }
    this.navigationAppearance =
      this.navigationAppearance === "default" ? "dense" : "default";
  }

  // ...

  private readonly searchCache = new Map<string, Navigation>();

  searchChanges() {
    this.form.controls.search.valueChanges.subscribe((value) => {
      const searchKey = value?.toLowerCase() || "";

      // Verificar cache
      if (this.searchCache.has(searchKey)) {
        this.navigation = this.searchCache.get(searchKey);
        this.cdRef.markForCheck();
        return;
      }

      // Crear nueva navegación filtrada
      const filteredNavigation: Navigation = {
        ...this.unmodifiedNavigation,
        default: searchKey
          ? this.unmodifiedNavigation.default.map((item) => ({
              ...item,
              children: item.children.filter((child) =>
                child.title.toLowerCase().includes(searchKey),
              ),
            }))
          : this.unmodifiedNavigation.default,
      };

      // Guardar en cache y aplicar
      this.searchCache.set(searchKey, filteredNavigation);
      this.navigation = filteredNavigation;
      this.cdRef.markForCheck();
    });
  }

  onClickItem() {
    this._router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.form.controls.search.setValue("");
      }
    });
  }

  setScheme(scheme: Scheme): void {
    this.fuseConfigurationService.config = { scheme };
  }
}
